// =============================================================================
// DeleteProjectModal.tsx — Patch 010.0.4 two-stage soft-delete confirmation.
//
// Per Dustin's directive: "i need to be able to delete a project if needed.
// big deal so make sure you have to confirm twice to delete."
//
// Stage 1 — warning callout, [Cancel] [Continue to confirm]
// Stage 2 — type-to-confirm address (exact match), [Cancel] [Delete project] (red)
//
// On success:
//   - calls onDeleted with the deleted project's id + deleted_at
//   - parent (Header) refreshes the project switcher and navigates away
//
// Server-side enforcement (routes/projects.ts):
//   - 404 project_not_found
//   - 403 synthetic_protected (Synthetic Variance Pilot is undeletable;
//     the Header Delete button also disables when current project is
//     is_synthetic=1, but server is the source of truth)
//   - 409 already_deleted (defensive; UI hides deleted projects so
//     this should be rare)
// =============================================================================

import { useEffect, useState } from 'react'

import { Modal } from './Modal'
import { ApiError, api } from '../lib/api'

type Stage = 'warning' | 'confirm'

interface DeleteProjectModalProps {
  open: boolean
  projectId: string
  projectDisplayName: string
  projectAddressStreet: string | null
  onClose: () => void
  onDeleted: (result: { projectId: string; deletedAt: string }) => void
}

export function DeleteProjectModal(props: DeleteProjectModalProps): JSX.Element | null {
  const [stage, setStage] = useState<Stage>('warning')
  const [confirmText, setConfirmText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset state every time the modal opens.
  useEffect(() => {
    if (props.open) {
      setStage('warning')
      setConfirmText('')
      setSubmitting(false)
      setError(null)
    }
  }, [props.open])

  if (!props.open) return null

  const expectedConfirm = (props.projectAddressStreet ?? '').trim()
  // Disable Delete unless the typed text exactly matches the address.
  // Empty address (rare; older projects without address_street) uses
  // a fallback comparison against the display name; flagged as a
  // schema-gate finding for future cleanup.
  const matchTarget = expectedConfirm.length > 0 ? expectedConfirm : props.projectDisplayName.trim()
  const matched = confirmText.trim() === matchTarget && matchTarget.length > 0

  async function onSubmit(): Promise<void> {
    if (!matched || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const r = await api.projects.delete(props.projectId)
      props.onDeleted({ projectId: props.projectId, deletedAt: r.deleted_at })
    } catch (e) {
      const msg =
        e instanceof ApiError ? `${e.status} ${e.message}` : (e as Error).message
      setError(msg)
      setSubmitting(false)
    }
  }

  if (stage === 'warning') {
    return (
      <Modal
        open={props.open}
        title="Delete project?"
        onClose={props.onClose}
        testId="delete-project-modal"
        footer={
          <>
            <button
              type="button"
              className="hcc-btn"
              onClick={props.onClose}
              data-testid="delete-project-cancel-stage1"
            >
              Cancel
            </button>
            <button
              type="button"
              className="hcc-btn hcc-btn--primary"
              onClick={() => setStage('confirm')}
              data-testid="delete-project-continue"
            >
              Continue to confirm
            </button>
          </>
        }
      >
        <p>
          Delete project <strong>{props.projectDisplayName}</strong>?
        </p>
        <p className="hcc-callout">
          This hides the project from the switcher; data stays in D1 for
          recovery.
        </p>
      </Modal>
    )
  }

  return (
    <Modal
      open={props.open}
      title="Confirm project delete"
      onClose={props.onClose}
      testId="delete-project-modal"
      footer={
        <>
          <button
            type="button"
            className="hcc-btn"
            onClick={props.onClose}
            data-testid="delete-project-cancel-stage2"
          >
            Cancel
          </button>
          <button
            type="button"
            className="hcc-btn hcc-btn--danger"
            onClick={onSubmit}
            disabled={!matched || submitting}
            data-testid="delete-project-confirm"
          >
            {submitting ? 'Deleting…' : 'Delete project'}
          </button>
        </>
      }
    >
      <p>
        Type the project's address <strong>{matchTarget}</strong> to enable
        Delete:
      </p>
      <input
        type="text"
        className="hcc-field__input"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder={matchTarget}
        autoFocus
        data-testid="delete-project-confirm-input"
      />
      {error ? (
        <p className="hcc-error" data-testid="delete-project-error">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}
